import type { DashboardSource, SloDayRow, TurnResult } from './slack-bot.types';

// What the page says, pure. Everything is in UTC, as the spend month and the
// delivery figures are

const MICRO = 1_000_000;

const dollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});
const wholeDollars = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function formatUsd(microUsd: number): string {
  if (microUsd === 0) return '$0.00';
  const usd = microUsd / MICRO;
  if (usd < 0.01) return '<$0.01';
  return usd >= 1000 ? wholeDollars.format(usd) : dollars.format(usd);
}

export function formatCount(count: number): string {
  return count.toLocaleString('en-US');
}

export function formatDuration(ms: number | null): string {
  if (ms === null) return '–';
  if (ms < 1000) return `${ms} ms`;
  // Under this a tenth still rounds below 10 s
  if (ms < 9950) return `${(ms / 1000).toFixed(1)} s`;
  // Rounded before it's split, so the seconds carry into the minutes
  const total = Math.round(ms / 1000);
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} s`;
}

// What's behind a job's run count, when anything is
export function runsNote(job: {
  readonly failed_runs: number;
  readonly declined_runs: number;
}): string {
  const parts = [
    job.failed_runs > 0 ? `${job.failed_runs} failed` : null,
    job.declined_runs > 0 ? `${job.declined_runs} declined` : null,
  ].filter((part) => part !== null);
  return parts.length === 0 ? '' : ` (${parts.join(', ')})`;
}

export function formatPercent(part: number, whole: number): string {
  if (whole === 0) return '–';
  const percent = (part / whole) * 100;
  if (part > 0 && percent < 1) return '<1%';
  return `${Math.round(percent)}%`;
}

const dateTime = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  timeZone: 'UTC',
});
const date = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

export function formatDateTime(at: string | null): string {
  return at === null ? '–' : dateTime.format(new Date(at));
}

export function formatDay(day: string): string {
  return date.format(new Date(`${day}T00:00:00Z`));
}

export const SOURCE_LABELS: Record<DashboardSource, string> = {
  mention: 'Mention',
  dm: 'DM',
  thread_reply: 'Thread reply',
  approval: 'Approval',
  untagged: 'Untagged',
  scheduled: 'Scheduled job',
  judge: 'Untagged judge',
  summaries: 'Channel summaries',
};

export const RESULT_LABELS: Record<TurnResult, string> = {
  answered: 'Answered',
  kept_quiet: 'Kept quiet',
  no_answer: 'No answer',
  continued: 'Went on',
  failed: 'Failed',
  stopped: 'Stopped',
  refused: 'Refused',
  running: 'Running',
};

const JUDGE_LABELS: Record<string, string> = {
  pending: 'Still deciding',
  replied: 'Replied',
  shadow_reply: 'Would have replied (shadow)',
  below_bar: 'Stayed quiet',
  capped: "Channel's hourly limit full",
  handled: 'A person replied first',
  gone: 'Message gone',
  no_room: 'No room under the cap',
  failed: 'Judge failed',
};

export const judgeLabel = (outcome: string): string =>
  JUDGE_LABELS[outcome] ?? outcome;

export function channelLabel(input: {
  kind: string | null;
  name: string | null;
  channelId: string | null;
}): string {
  if (input.kind === 'im') return 'DM';
  if (input.kind === 'mpim') return 'Group DM';
  const name = input.name ?? input.channelId ?? 'Unknown channel';
  return input.kind === 'private' ? `${name} (private)` : `#${name}`;
}

export function personLabel(input: {
  name: string | null;
  slackUserId: string;
}): string {
  return input.name ?? input.slackUserId;
}

export function jobLabel(job: {
  title: string | null;
  channel_kind: string | null;
}): string {
  if (job.title !== null) return job.title;
  if (job.channel_kind === 'im') return 'A job in a DM';
  if (job.channel_kind === 'mpim') return 'A job in a group DM';
  if (job.channel_kind === 'private') return 'A job in a private channel';
  if (job.channel_kind === 'public')
    return 'A job posting to a private channel';
  return 'A job';
}

const BREACH_LABELS: Record<SloDayRow['breaches'][number], string> = {
  missed: 'Missed mention',
  first_status: 'Slow first status',
  unanswered: 'No answer in time',
};

// A day's figures across its triggers: counts add up, while a percentile
// doesn't, so the slowest trigger's shows
export interface DeliveryDay {
  day: string;
  turns: number;
  swept: number;
  statusWithin: number;
  statusEligible: number;
  answeredLate: number;
  silent: number;
  worstAnswerP95Ms: number | null;
  breaches: string[];
}

export function deliveryDays(rows: readonly SloDayRow[]): DeliveryDay[] {
  const days = new Map<string, DeliveryDay>();
  for (const row of rows) {
    const day = days.get(row.day) ?? {
      day: row.day,
      turns: 0,
      swept: 0,
      statusWithin: 0,
      statusEligible: 0,
      answeredLate: 0,
      silent: 0,
      worstAnswerP95Ms: null,
      breaches: [],
    };
    day.turns += row.turns;
    day.swept += row.swept;
    day.statusWithin += row.first_status.within_target;
    day.statusEligible += row.first_status.eligible;
    day.answeredLate += row.answer.late;
    day.silent += row.answer.silent;
    if (row.answer.p95_ms !== null) {
      day.worstAnswerP95Ms = Math.max(
        day.worstAnswerP95Ms ?? 0,
        row.answer.p95_ms
      );
    }
    for (const breach of row.breaches) {
      const label = BREACH_LABELS[breach];
      if (!day.breaches.includes(label)) day.breaches.push(label);
    }
    days.set(row.day, day);
  }
  return [...days.values()].sort((a, b) => b.day.localeCompare(a.day));
}
